/**
 * Shared utility functions used across multiple pages.
 */

import {
  getImageSource,
  inferUrlProvider,
  normalizeImageRefs as sharedNormalizeImageRefs,
  selectPreferredImageUrl,
} from "@applauss/api-client";

export const parseCommaSeparated = (value) =>
  value ? value.split(",").map((s) => s.trim()).filter(Boolean) : [];

export const arraysEqual = (a, b) => JSON.stringify(a) === JSON.stringify(b);

export const normalizeImageRefs = sharedNormalizeImageRefs;

/**
 * Apply a URL edit to a ticket-URL row, filling `provider` from the new URL so the admin
 * sees the value that will be stored instead of a blank field.
 *
 * A hand-typed provider is never overwritten — only an empty field or a previous auto-fill
 * is refreshed. `_providerAuto` is UI-only state; `normalizeUrlRefs` drops it before the
 * update payload is built.
 */
export const applyUrlRefUrlChange = (urlRef = {}, nextUrl) => {
  if (urlRef.provider && !urlRef._providerAuto) return { ...urlRef, url: nextUrl };
  const provider = inferUrlProvider(nextUrl);
  return { ...urlRef, url: nextUrl, provider, _providerAuto: !!provider };
};

export const selectBestImageUrl = selectPreferredImageUrl;

// Surviving URLs keep their source; a newly added one is credited to its own provider, never
// to Applauss — `source` is the public image credit and we host none of these images.
export const reconcileImageUrls = (currentImages, nextUrls) => {
  const byUrl = new Map(normalizeImageRefs(currentImages).map((image) => [image.url, image]));
  return nextUrls.map((url) => byUrl.get(url) || { url, source: getImageSource(url) });
};

/** "1 event", "2 events", "1,204 events" — counts are routinely 1, and "1 events" reads as a bug. */
export const plural = (count, noun) => `${Number(count).toLocaleString()} ${count === 1 ? noun : `${noun}s`}`;

export const capitalize = (s) =>
  s ? s.charAt(0).toUpperCase() + s.slice(1) : s;

/** A classification value as a label: a plain string, or a `{id, name}` ref (themes). */
export const formatClassificationItem = (item) => {
  if (!item) return "";
  if (typeof item === "string") return capitalize(item);
  return capitalize(item.name || item.id || "");
};

export const classificationList = (value) =>
  Array.isArray(value) ? value.map(formatClassificationItem).filter(Boolean) : [];

export const formatDate = (dateString) => {
  if (!dateString) return "-";
  // Append T00:00 to date-only strings to force local-time parsing (avoid UTC midnight shifting the day)
  const normalized = /^\d{4}-\d{2}-\d{2}$/.test(dateString) ? `${dateString}T00:00` : dateString;
  return new Date(normalized).toLocaleDateString("en-US", { day: "2-digit", month: "short", year: "numeric" });
};

// Resolve the consumer web app base URL for "view on site" links: explicit
// VITE_WEB_URL first, otherwise derive it from the current origin by stripping the
// `admin.` subdomain (admin.staging.applauss.com -> staging.applauss.com). Empty
// string when it can't be derived (a localhost/IP dev server on a different port);
// set VITE_WEB_URL in the root .env for local dev.
function resolveWebBaseUrl() {
  const configured = import.meta.env.VITE_WEB_URL;
  if (configured) return configured;
  if (typeof window === "undefined") return "";
  const { protocol, hostname } = window.location;
  if (hostname === "localhost" || /^[\d.]+$/.test(hostname)) return "";
  return `${protocol}//${hostname.replace(/^admin\./, "")}`;
}
export const APPLAUSS_WEB_URL = resolveWebBaseUrl();

// Did an artist delete leave cleanup to do? `refs_remaining` is 0 when the cascade was
// verified clean, a positive count when refs survived a rejected bulk write, and null when the
// check itself could not run. Only 0 is reassuring, so anything else belongs in the warning
// banner rather than the success one. Older responses omit the field entirely — treat that as
// nothing to report rather than as a problem.
export const artistDeleteNeedsAttention = (result) =>
  result?.refs_remaining !== undefined && result.refs_remaining !== 0;

// Consumer web app URL for an event's detail page, or null when the web base
// URL can't be resolved — callers should hide the link rather than point at a
// bad host. `lang` opens it in that language for that view (the web reads `?lang=`).
export const getEventWebUrl = (eventId, { lang } = {}) => {
  if (!APPLAUSS_WEB_URL) return null;
  const params = new URLSearchParams({ id: eventId, ...(lang ? { lang } : {}) });
  return `${APPLAUSS_WEB_URL}/event-details?${params}`;
};

// In-admin link to an entity's detail modal. Each list page opens `?<type>=<id>`
// on load (see useEntityDeepLink), so the query param name is the entity type.
// Null for an unknown type or a missing id, so callers can hide the link.
const ADMIN_ENTITY_PATHS = {
  event: "/events",
  artist: "/artists",
  venue: "/venues",
  genre: "/genres",
};
export const getAdminEntityUrl = (entityType, entityId) =>
  ADMIN_ENTITY_PATHS[entityType] && entityId
    ? `${ADMIN_ENTITY_PATHS[entityType]}?${entityType}=${encodeURIComponent(entityId)}`
    : null;

export const formatDateTime = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString("en-US");
};

export const hasClassificationProfile = (profile) =>
  !!profile && typeof profile === "object" && Object.keys(profile).length > 0;

export const emptyImageReviewStats = () => ({
  total: 0,
  eventImageFallbackCount: 0,
  artistFallbackCount: 0,
  badImageCount: 0,
});

export const normalizeImageReviewStats = (data = {}) => ({
  total: data.total || 0,
  eventImageFallbackCount: data.event_image_fallback_count || 0,
  artistFallbackCount: data.artist_fallback_count || 0,
  badImageCount: data.bad_image_count || 0,
});

const IMAGE_REVIEW_STAT_BY_STATUS = {
  event_fallback: "eventImageFallbackCount",
  artist_fallback: "artistFallbackCount",
  bad: "badImageCount",
};

export const removeFromImageReviewStats = (stats, resolutionStatus) => {
  const countKey = IMAGE_REVIEW_STAT_BY_STATUS[resolutionStatus];
  if (!countKey) return stats;
  return {
    ...stats,
    total: Math.max(0, stats.total - 1),
    [countKey]: Math.max(0, stats[countKey] - 1),
  };
};

/**
 * What consumers see, for any record with an admin-curated label: the curated value beats the
 * provider's own (`preferred_name` on a venue or artist, `preferred_title` on an event; same
 * contract as `getEventVenueName`/`getEventDescription` in the web app, and the same name set
 * the API's `venue_name_set` compares). Every such label in the admin app goes through this —
 * showing the raw provider string would show the admin something users no longer see. The
 * provider's string is not lost: `ProviderLabel` renders it underneath.
 */
export const displayLabel = (preferred, original) => preferred || original || "";

export const venueDisplayName = (venue) => displayLabel(venue?.preferred_name, venue?.name);

// The id every venue endpoint reads and writes by: the document id (`_id`, on list rows and on a
// venue opened by id). A legacy venue's `_source.id` differs from it, and room links store it.
export const venueDocId = (venue) => venue?._id || venue?.id;

// Display names for the ingestion sources — the keys of `INGESTION_SOURCES` in
// `shared/constants/ingestion.py`, which JS cannot import. It is also the list the events page
// offers as source-filter options, so a source added to the Python constant is reachable by the
// API but absent from that dropdown until it is added here too.
export const SOURCE_LABELS = {
  ticketmaster: "Ticketmaster",
  jambase: "JamBase",
  opendata_paris: "OpenData Paris",
  opendata_madrid: "OpenData Madrid",
};

export const sourceLabel = (source) => SOURCE_LABELS[source] || source;

// A decimal price typed by an admin, or null when it is not one.
//
// Shared by the reports modal and the event-research card: both let an admin correct a proposed
// amount before accepting, and both must treat "22,50" as 22.5 rather than handing the API a NaN.
// `Number("22,50")` is NaN, which JSON-encodes as null and comes back as an opaque 422.
export const DECIMAL_PRICE_PATTERN = /^(?:\d+(?:[.,]\d+)?|[.,]\d+)$/;

export function parsePriceInput(value) {
  const normalized = String(value ?? "").trim();
  if (!DECIMAL_PRICE_PATTERN.test(normalized)) return null;
  const price = Number(normalized.replace(",", "."));
  // 0 is a price: free admission, which the event should say.
  return Number.isFinite(price) && price >= 0 ? price : null;
}

// A billing position typed by an admin, as the API takes it: a whole number >= 1, or null.
//
// Shared by the research card (proposed artists and the event's own line-up) and the events page.
// Blank means unranked, and null is what clears a rank, so an empty box parses to null on purpose.
// Everything else that is not a whole number >= 1 parses to null too, but that null means
// "unusable" rather than "unranked" — `rankIsUnusable` is what tells the two apart, and it is why
// this must not round: `parseInt("1.9")` is 1, which would silently set a headliner nobody asked
// for. `type="number"` keeps most of this out of the box already; a pasted value does not have to
// pass through it.
export function parseRankInput(value) {
  const rank = Number(String(value ?? "").trim());
  return Number.isInteger(rank) && rank >= 1 ? rank : null;
}

/** A box holding something that is neither blank nor a usable rank — "0", "2.5", "abc". */
export function rankIsUnusable(value) {
  return String(value ?? "").trim() !== "" && parseRankInput(value) === null;
}

/** "headliner" / "billing #3" for a rank, or "" when there is none to show. */
export function rankLabel(rank) {
  if (!rank) return "";
  return rank === 1 ? "headliner" : `billing #${rank}`;
}

/**
 * Entries of a provider-id / link map, minus the ones that hold no value.
 *
 * Elasticsearch has no way to drop a key from an object field through a partial update, so
 * clearing one writes an explicit `null` (the artist MBID change does this to the identity
 * `source_ids`). The key stays in `_source` forever, and the admin endpoints return the raw
 * document — read it with `Object.keys` and a cleared provider renders as a badge with nothing
 * behind it, or as an empty row in the editor that reads as an unsaved change.
 */
export function definedEntries(map) {
  return Object.entries(map || {}).filter(([, value]) => value !== null && value !== undefined && value !== "");
}

/**
 * "40 replies cut off (content_filter)" from an LLM step's `incomplete` metric — a `{reason: count}`
 * map of classification replies a content filter or an output cap stopped — or "" when there were none.
 *
 * Not a warning (the API does not raise one): each of those entities is already marked ambiguous
 * like any failed classification, and this is the only place the page says why.
 */
export function formatCutOffReplies(incomplete) {
  const entries = Object.entries(incomplete || {}).filter(([, count]) => Number(count) > 0);
  if (!entries.length) return "";
  const total = entries.reduce((sum, [, count]) => sum + Number(count), 0);
  const reasons =
    entries.length === 1 ? entries[0][0] : entries.map(([reason, count]) => `${reason} ${Number(count)}`).join(", ");
  // "en-US" like every other number on the pipeline-runs page, so a count never shows two separators.
  return `${total.toLocaleString("en-US")} ${total === 1 ? "reply" : "replies"} cut off (${reasons})`;
}

/**
 * The muted note under an LLM step, run or range: its cut-off replies (`formatCutOffReplies`) and the
 * turns whose cost is an estimate — a timeout or a lost connection OpenAI may still have billed,
 * charged what was sent plus the output cap (`estimated_turns`). "" when there is neither.
 */
export function formatLlmReplyNotes(incomplete, estimatedTurns) {
  const turns = Number(estimatedTurns) || 0;
  const estimated = turns
    ? `${turns.toLocaleString("en-US")} ${turns === 1 ? "turn" : "turns"} at estimated cost (timeout or lost connection)`
    : "";
  return [formatCutOffReplies(incomplete), estimated].filter(Boolean).join(" · ");
}

// " — will try again after 3 Sep" for a research marker carrying a retry window.
//
// Shared by the event- and artist-research screens: both read the same `{verdict, retry_after}`
// marker shape off their run, and the wording is what tells an admin that an empty section is a
// pending retry rather than a settled answer. One copy, so a copy or locale fix lands on both.
export const retryHint = (marker) => {
  if (!marker?.retry_after) return "";
  const when = new Date(marker.retry_after);
  if (Number.isNaN(when.getTime())) return "";
  return ` — will try again after ${when.toLocaleDateString(undefined, { day: "numeric", month: "short" })}`;
};

// English display name for a locale code (the API's SUPPORTED_LOCALES / TRANSLATION_TARGET_LOCALES);
// a code the browser cannot name shows as the code itself.
export const languageName = (() => {
  try {
    const names = new Intl.DisplayNames(["en"], { type: "language" });
    return (code) => names.of(code) || code;
  } catch {
    return (code) => code;
  }
})();

// The message to show for a failed API call: the API's string `detail`, or a validation 422's
// messages joined (FastAPI sends that `detail` as an array of objects, which React cannot render),
// else `fallback`.
export const apiErrorMessage = (err, fallback) => {
  const detail = err?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  return (Array.isArray(detail) && detail.map((d) => d?.msg).filter(Boolean).join("; ")) || fallback;
};

/**
 * A run time or minimum age typed in the editor (#792): `{value}` — null for blank — or `{error}`.
 * Digits only, because parseInt would save "2.5" as 2 and Number would take "1e2" or "0x5A", and
 * within the API's bounds, so an out-of-range value never reaches a 422.
 */
export function parseWholeNumberInput(raw, label, max) {
  const text = String(raw ?? "").trim();
  if (text === "") return { value: null };
  const value = /^\d+$/.test(text) ? Number(text) : NaN;
  return value >= 1 && value <= max ? { value } : { error: `${label} must be a whole number from 1 to ${max}` };
}
